import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import type {
  CSSProperties,
  FocusEventHandler,
  PointerEventHandler,
  ReactNode,
} from "react";

type InteractiveLayerId =
  | "left-sidebar"
  | "right-panel"
  | "canvas-left-rail"
  | "comment-popover"
  | "input-bar"
  | "selection-toolbar"
  | "session-popover"
  | "notification-popover"
  | "search-hub"
  | "task-pocket"
  | "settings-pocket"
  | "about-denker"
  | "frame-move-project-modal";

interface InteractiveLayerState {
  counter: number;
  order: Partial<Record<InteractiveLayerId, number>>;
}

type InteractiveLayerTier = "widget" | "menu" | "modal";

interface InteractiveLayerContextValue {
  bringToFront: (id: InteractiveLayerId) => void;
  getZIndex: (
    id: InteractiveLayerId,
    baseZIndex: number,
    tier: InteractiveLayerTier,
  ) => number;
}

interface UseInteractiveLayerOptions {
  active?: boolean;
  baseZIndex: number;
  tier?: InteractiveLayerTier;
}

const PROMOTED_Z_INDEX_BASE = 20_000;
// Glass menus sit above fullscreen presentation views (30k), while app
// modals sit above menus and below confirm/onboarding overlays. Menus
// opened from INSIDE a modal re-promote above it via data-modal-menu
// (ModalSurfaceScope + .denker-glass-menu[data-modal-menu] in index.css).
const MENU_Z_INDEX_BASE = 31_000;
const PROMOTED_MENU_Z_INDEX_MAX = 31_499;
const MODAL_Z_INDEX_BASE = 31_500;
const TOAST_Z_INDEX = MODAL_Z_INDEX_BASE - 1;
const PROMOTED_WIDGET_Z_INDEX_MAX = TOAST_Z_INDEX - 1;

const InteractiveLayerContext =
  createContext<InteractiveLayerContextValue | null>(null);

function layerReducer(
  state: InteractiveLayerState,
  id: InteractiveLayerId,
): InteractiveLayerState {
  const orderedLayers = (
    Object.entries(state.order) as Array<
      [InteractiveLayerId, number | undefined]
    >
  )
    .filter(
      (entry): entry is [InteractiveLayerId, number] =>
        entry[0] !== id && typeof entry[1] === "number",
    )
    .sort((a, b) => a[1] - b[1]);
  const order: Partial<Record<InteractiveLayerId, number>> = {};
  orderedLayers.forEach(([layerId], index) => {
    order[layerId] = index + 1;
  });
  const nextCounter = orderedLayers.length + 1;
  return {
    counter: nextCounter,
    order: {
      ...order,
      [id]: nextCounter,
    },
  };
}

function zIndexFor(
  state: InteractiveLayerState,
  id: InteractiveLayerId,
  baseZIndex: number,
  tier: InteractiveLayerTier,
) {
  const promotedOrder = state.order[id];
  if (tier === "modal") {
    // Modal tier floors at MODAL_Z_INDEX_BASE; promotion still orders
    // modals among themselves (settings over search after a click).
    return MODAL_Z_INDEX_BASE + (promotedOrder ?? 0);
  }
  if (tier === "menu") {
    return promotedOrder
      ? Math.min(MENU_Z_INDEX_BASE + promotedOrder, PROMOTED_MENU_Z_INDEX_MAX)
      : Math.max(baseZIndex, MENU_Z_INDEX_BASE);
  }
  return promotedOrder
    ? Math.min(
        PROMOTED_Z_INDEX_BASE + promotedOrder,
        PROMOTED_WIDGET_Z_INDEX_MAX,
      )
    : baseZIndex;
}

function InteractiveLayerProvider({ children }: { children: ReactNode }) {
  const parent = useContext(InteractiveLayerContext);
  const [state, dispatch] = useReducer(layerReducer, {
    counter: 0,
    order: {},
  });

  const value = useMemo<InteractiveLayerContextValue>(
    () => ({
      bringToFront: dispatch,
      getZIndex: (id, baseZIndex, tier) =>
        zIndexFor(state, id, baseZIndex, tier),
    }),
    [state],
  );

  return (
    <InteractiveLayerContext.Provider value={parent ?? value}>
      {children}
    </InteractiveLayerContext.Provider>
  );
}

function useInteractiveLayer(
  id: InteractiveLayerId,
  { active = true, baseZIndex, tier = "widget" }: UseInteractiveLayerOptions,
) {
  const context = useContext(InteractiveLayerContext);
  const promote = context?.bringToFront;
  const bringToFront = useCallback(() => {
    if (active) promote?.(id);
  }, [active, id, promote]);
  const zIndex = context?.getZIndex(id, baseZIndex, tier) ?? baseZIndex;
  const style = useMemo<CSSProperties>(() => ({ zIndex }), [zIndex]);
  const onPointerDownCapture = useCallback<PointerEventHandler>(
    () => bringToFront(),
    [bringToFront],
  );
  const onFocusCapture = useCallback<FocusEventHandler>(
    () => bringToFront(),
    [bringToFront],
  );

  useEffect(() => {
    if (active) bringToFront();
  }, [active, bringToFront]);

  return {
    bringToFront,
    interactionProps: {
      onFocusCapture,
      onPointerDownCapture,
    },
    style,
    zIndex,
  };
}

/* eslint-disable react-refresh/only-export-components */
export {
  InteractiveLayerProvider,
  MENU_Z_INDEX_BASE,
  TOAST_Z_INDEX,
  useInteractiveLayer,
};
/* eslint-enable react-refresh/only-export-components */
export type { InteractiveLayerId, InteractiveLayerTier };
